import '@beyondtrust/btux-iconography-web';
import { ContextProvider } from '@lit/context';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import { localized, msg } from '@lit/localize';
import { Task, TaskStatus } from '@lit/task';
import { LitElement, PropertyValues, TemplateResult, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { keyed } from 'lit/directives/keyed.js';
import { repeat } from 'lit/directives/repeat.js';
import { PLATFORM_HOME_PRODUCT_MENU_ITEM, ProductAPIMap, hasProductMenu } from '../../constants';
import { APIMenuSet, menuDataContext } from '../../context/menu-data';
import { LaunchDarklyController } from '../../controllers/launch-darkly-controller';
import { SidebarFocusHost, SidebarPanelFocusController } from '../../controllers/sidebar-panel-focus-controller';
import { flagContext } from '../../feature-flags';
import '../menu/defaults/reporting-menu/reporting-insights-menu';
import '../menu/defaults/reporting-menu/reporting-menu';
import {
  resolveCurrentProduct,
  resolveFirstLevelExtras,
  resolveProductMenuItems,
} from '../../selectors/menu-selectors';
import { getProductMenu, getSiteData, getUserSiteConfigs, selectSite } from '../../services/menu-data-store';
import { t } from '../../services/runtime-i18n';
import { ProductMenuItem } from '../../types';
import '../product-icon/product-icon';
import { MENU_SIDEBAR_PANEL_TAG, PlatformMenuSidebarPanel } from './menu-sidebar-panel';
import styles from './menu-sidebar.styles.scss';

export const MENU_SIDEBAR_TAG = 'platform-menu-sidebar' as const;

/** localStorage key persisting the expanded state across page loads. */
export const MENU_SIDEBAR_EXPANDED_KEY = 'platformMenuSidebarExpanded';

/**
 * localStorage key persisting the pinned state across tabs and page loads
 * (matching the expanded rail). Only the boolean persists — a user may switch
 * sites (with different entitlements), so which panel to reopen is derived from
 * the URL's current product, never stored.
 */
export const MENU_SIDEBAR_PINNED_KEY = 'platformMenuSidebarPinned';

/**
 * Standalone sidebar navigation, placed by consumers alongside
 * `<platform-header navigation-layout="sidebar">` in the 2026 layout.
 *
 * Owns the page's primary navigation landmark: a labelled `<nav>` wraps whatever
 * menu content ends up inside. Content nested here later must not introduce a
 * second navigation landmark of its own.
 *
 * First-level content: one button per product the current site is entitled to,
 * resolved from the shared menu-data-store (deduped with platform-header's own
 * fetches) through the same selector the overlay menu uses.
 *
 * Consumer menu replacement: content slotted under a product's slot name
 * (e.g. `slot="insights-menu"` — the same names the overlay contract uses)
 * renders in the second-level panel instead of that product's API menu.
 *
 * @public
 */
@customElement(MENU_SIDEBAR_TAG)
@localized()
export class PlatformMenuSidebar extends LitElement implements SidebarFocusHost {
  static override styles = [styles];

  /**
   * Expanded rail (25.6rem) when set; collapsed rail (4.8rem) when absent.
   * Persisted to localStorage on change; a stored value overrides the initial
   * attribute when the element connects. @public
   */
  @property({ type: Boolean, reflect: true })
  expanded = false;

  /**
   * Pinned second-level panel: pushes page content instead of floating over it
   * and survives outside clicks. Persisted to localStorage on change; a stored
   * value overrides the initial attribute when the element connects. @public
   */
  @property({ type: Boolean, reflect: true })
  pinned = false;

  /** The open second-level panel: a product's menu or a non-product panel. */
  @state()
  private _openPanel?: ProductMenuItem | 'reporting' | 'more-products';

  // First-level nav: the products entitled on the current site, same resolution
  // path as the overlay menu (platform-header shares the underlying fetch).
  private _products = new Task(this, {
    task: async () => resolveProductMenuItems(await getSiteData()),
    args: () => [],
  });

  // Current site data: used to determine residency for PathfinderAI visibility.
  private _currentSite = new Task(this, {
    task: () => getSiteData(),
    args: () => [],
  });

  // Second-level nav: the open product's menu (store-deduped with the overlay's
  // own fetch of the same product; static menus resolve without a request).
  // The result carries its product slug: Task keeps the previous value while a
  // new run is in flight, and product A's menu must never render under product
  // B's heading (with B's baseURL) during the swap.
  private _panelMenu = new Task(this, {
    task: async ([product]) =>
      product && hasProductMenu(product.slug) ? { slug: product.slug, menu: await getProductMenu(product.slug) } : null,
    args: () => [typeof this._openPanel === 'object' ? this._openPanel : undefined] as const,
  });

  // The slotted platform-reporting-menu consumes these contexts, which the
  // header normally provides — the sidebar provides its own copies here.
  private _menuDataProvider = new ContextProvider(this, { context: menuDataContext });
  private _flagsProvider = new ContextProvider(this, { context: flagContext });

  // Reporting aggregates every entitled product's menu; fetch them (store-deduped)
  // when the Reporting panel opens and pipe them to the context provider.
  // Not private: the task exists for its side effect and is never read.
  _reportingMenus = new Task(this, {
    task: async ([open, products]) => {
      if (open !== 'reporting' || products == undefined) {
        return;
      }
      const apiProducts = products.filter((p) => ProductAPIMap[p.slug]);
      const results = await Promise.allSettled(
        apiProducts.map(async (p) => [p.slug, await getProductMenu(p.slug)] as const),
      );
      const menuData: APIMenuSet = {};
      for (const result of results) {
        if (result.status === 'fulfilled') {
          menuData[result.value[0]] = result.value[1];
        }
      }
      this._menuDataProvider.setValue(menuData);
    },
    args: () => [this._openPanel, this._products.value] as const,
  });

  // Reopen the panel for the URL's current product once products resolve — never
  // a stored product, which could be unentitled after a site switch. Latched at
  // connect time: only a pinned state persisted from a previous page restores a
  // panel; pinning within this document must not swap the panel the user opened.
  private _restorePinnedPanel = false;

  // Shared with platform-header: the controller's client/flags are module-scoped,
  // so this opens no second LD connection when the header is on the page — and
  // still works standalone, where the sidebar initializes the shared client itself.
  private launchDarklyController = new LaunchDarklyController(this);

  // Moves focus into the second-level panel on keyboard activation of a tier-1
  // trigger (ARIA APG Disclosure Navigation); pointer activation leaves focus
  // alone. See SidebarFocusHost members below for the state it reads/mutates.
  private panelFocusController = new SidebarPanelFocusController(this);

  // Site configs feed the Administration link (it needs the GLOBAL site to switch to).
  private _sites = new Task(this, {
    task: () => getUserSiteConfigs(),
    args: () => [],
  });

  override connectedCallback() {
    super.connectedCallback();
    this.launchDarklyController.bootstrap();
    try {
      const storedExpanded = window.localStorage.getItem(MENU_SIDEBAR_EXPANDED_KEY);
      if (storedExpanded !== null) {
        this.expanded = storedExpanded === 'true';
      }
      // Guard on a stored value like `expanded`, so a markup `pinned` default survives.
      const storedPinned = window.localStorage.getItem(MENU_SIDEBAR_PINNED_KEY);
      if (storedPinned !== null) {
        this.pinned = storedPinned === 'true';
      }
      // Restore a panel only for pinned state persisted from a previous page, never a
      // markup default — that would open a panel the consumer never asked to restore.
      this._restorePinnedPanel = storedPinned !== null && this.pinned;
    } catch {
      // Storage unavailable (private mode / disabled): fall back to markup state.
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.onDocumentClick);
    this._listeningForOutsideClicks = false;
  }

  protected override willUpdate() {
    // Styling hook for the pinned-open pushed layout (see menu-sidebar.styles.scss).
    this.toggleAttribute('panel-open', this._openPanel != undefined);
    // Keep the slotted reporting menu's flag context in step with the shared client.
    if (this._flagsProvider.value !== this.launchDarklyController.flags) {
      this._flagsProvider.setValue(this.launchDarklyController.flags);
    }
  }

  protected override updated(changed: PropertyValues<this>) {
    if (changed.has('expanded')) {
      try {
        window.localStorage.setItem(MENU_SIDEBAR_EXPANDED_KEY, String(this.expanded));
      } catch {
        // Storage unavailable: state simply won't persist across reloads.
      }
    }
    if (changed.has('pinned')) {
      try {
        window.localStorage.setItem(MENU_SIDEBAR_PINNED_KEY, String(this.pinned));
      } catch {
        // Storage unavailable: state simply won't persist across reloads.
      }
    }

    if (this._restorePinnedPanel && this._products.value) {
      this._restorePinnedPanel = false;
      const current = resolveCurrentProduct(this._products.value, window.location.pathname);
      // Still pinned and nothing opened in the meantime: don't stomp user actions.
      if (
        this.pinned &&
        this._openPanel == undefined &&
        current !== PLATFORM_HOME_PRODUCT_MENU_ITEM &&
        hasProductMenu(current.slug)
      ) {
        this._openPanel = current;
      }
    }

    // An unpinned open panel closes on any click outside the sidebar.
    const shouldListen = this._openPanel != undefined && !this.pinned;
    if (shouldListen !== this._listeningForOutsideClicks) {
      this._listeningForOutsideClicks = shouldListen;
      if (shouldListen) {
        document.addEventListener('click', this.onDocumentClick);
      } else {
        document.removeEventListener('click', this.onDocumentClick);
      }
    }
  }

  private _listeningForOutsideClicks = false;

  private onDocumentClick = (event: Event) => {
    if (!event.composedPath().includes(this)) {
      this.closePanel();
    }
  };

  /** SidebarFocusHost: whether a second-level panel is open. */
  get panelOpen(): boolean {
    return this._openPanel != undefined;
  }

  /** SidebarFocusHost: clear the open-panel state. */
  closePanel() {
    this._openPanel = undefined;
  }

  /** SidebarFocusHost: the live panel element. */
  queryPanel(): PlatformMenuSidebarPanel | null {
    return this.shadowRoot?.querySelector(MENU_SIDEBAR_PANEL_TAG) ?? null;
  }

  /**
   * SidebarFocusHost: the tier-1 trigger whose panel is open. Scoped to `li` on
   * purpose — the rail's own expand/collapse button also carries aria-expanded
   * and must not be mistaken for a panel trigger.
   */
  queryOpenTrigger(): HTMLElement | null {
    return this.shadowRoot?.querySelector<HTMLElement>('.rail li [aria-expanded="true"]') ?? null;
  }

  private onProductClick(product: ProductMenuItem, event: MouseEvent) {
    this.panelFocusController.noteActivation(event);
    if (typeof this._openPanel === 'object' && this._openPanel.slug === product.slug) {
      this.closePanel();
      return;
    }
    // Products without menu content keep their first-level button inert for now.
    if (hasProductMenu(product.slug)) {
      this._openPanel = product;
      this.panelFocusController.requestPanelFocus();
    }
  }

  private onReportingClick(event: MouseEvent) {
    this.panelFocusController.noteActivation(event);
    if (this._openPanel === 'reporting') {
      this.closePanel();
      return;
    }
    this._openPanel = 'reporting';
    this.panelFocusController.requestPanelFocus();
  }

  private onMoreProductsClick(event: MouseEvent) {
    this.panelFocusController.noteActivation(event);
    if (this._openPanel === 'more-products') {
      this.closePanel();
      return;
    }
    this._openPanel = 'more-products';
    this.panelFocusController.requestPanelFocus();
  }

  private togglePinned() {
    this.pinned = !this.pinned;
  }

  // Keyboard events are composed, so Escape pressed inside the panel's shadow
  // root bubbles out to this listener too.
  private onNavKeydown(event: KeyboardEvent) {
    this.panelFocusController.handleKeydown(event);
  }

  /**
   * Selecting a nav link dismisses an unpinned panel, so the user is not left
   * tabbing back through the whole menu to reach page content.
   *
   * Skips cancelled activations: DirtyFormController calls preventDefault() from
   * the panel's own shadow root when a form is dirty, and this listener runs
   * afterwards. Closing then would pull the panel out from under a user who is
   * being asked to confirm leaving and elects to stay.
   *
   * Restores focus to the trigger when the activated link held it (a keyboard
   * Enter, or the auto-focus this feature itself moves onto the panel's first
   * link): removing the panel destroys that link outright for the
   * Angular/React consumer shells that intercept clicks for client-side
   * routing -- there is no page unload to reset focus in that case, so
   * without this, focus falls back to `<body>` and the user's next Tab
   * restarts at the top of the document. `:focus` matches the link itself
   * regardless of which shadow root it lives in, so this holds for consumer-
   * slotted links too. Mirrors handleKeydown's Escape restoration.
   */
  private onPanelClick(event: MouseEvent) {
    if (this.pinned || event.defaultPrevented) {
      return;
    }
    const link = event
      .composedPath()
      .find((node): node is HTMLAnchorElement => node instanceof HTMLAnchorElement && node.href !== '');
    if (link == undefined) {
      return;
    }
    // Capture before closing: closing re-renders the rail and the trigger
    // loses its aria-expanded="true" match.
    const trigger = link.matches(':focus') ? this.queryOpenTrigger() : undefined;
    this.closePanel();
    if (trigger) {
      void this.updateComplete.then(() => trigger.focus());
    }
  }

  override render() {
    return html`
      <nav
        aria-label=${msg('Product navigation', {
          desc: 'Accessible name for the primary sidebar navigation landmark',
        })}
        @keydown=${this.onNavKeydown}
      >
        <div class="rail">
          <button
            type="button"
            aria-expanded=${this.expanded}
            aria-label=${msg('Toggle sidebar navigation', {
              desc: 'Button that expands or collapses the sidebar navigation',
            })}
            @click=${this.toggleExpanded}
          >
            ${keyed(
              this.expanded,
              // btux-svg only reads its name at first render (a class field shadows the
              // reactive property), so re-create the element instead of mutating it.
              html`<btux-svg name=${this.expanded ? 'menu-uncollapsed' : 'menu-collapsed'}></btux-svg>`,
            )}
          </button>
          <div class="rail-scroll">
            ${this._products.render({
              complete: (products) => this.renderFirstLevel(products),
            })}
            <slot></slot>
          </div>
          ${this._products.render({
            complete: (products) => this.renderAdmin(products),
          })}
        </div>
        ${this.renderPanel()}
      </nav>
    `;
  }

  /** The 'Reporting' heading is deliberately English — see renderFirstLevel below. */
  private renderPanel() {
    const open = this._openPanel;
    if (open == undefined) {
      return nothing;
    }
    const product = typeof open === 'object' ? open : undefined;
    // Consumer menu-replacement contract: content slotted on the sidebar under the
    // product's slot name (e.g. slot="insights-menu", same names as the overlay)
    // takes precedence over the product's API-driven menu.
    const consumerMenu = product != undefined && this.querySelector(`[slot='${product.menuSlotName}']`) != null;
    // Only forward a menu fetched for this product — the task's value is the
    // previous product's menu until the new fetch resolves.
    const fetched = this._panelMenu.value;
    const menu = product && !consumerMenu && fetched?.slug === product.slug ? fetched.menu : undefined;
    // A fresh panel per product: resets accordion state and avoids btux-svg's
    // mutate-in-place pitfall when swapping products.
    return keyed(
      product?.slug ?? open,
      html`
        <platform-menu-sidebar-panel
          .product=${product}
          .menu=${menu}
          ?menuError=${this._panelMenu.status === TaskStatus.ERROR}
          heading=${product ? '' : open === 'reporting' ? 'Reporting' : msg('More Products')}
          .flags=${this.launchDarklyController.flags}
          ?pinned=${this.pinned}
          ?consumerMenu=${consumerMenu}
          @pin-toggle=${this.togglePinned}
          @click=${this.onPanelClick}
        >
          ${product
            ? consumerMenu
              ? html`<slot name=${product.menuSlotName}></slot>`
              : nothing
            : open === 'reporting'
              ? html`<platform-reporting-menu .productMenuItems=${this._products.value ?? []}>
                  <slot name="reporting-insights-menu" slot="reporting-insights-menu">
                    <platform-reporting-insights-menu></platform-reporting-insights-menu>
                  </slot>
                </platform-reporting-menu>`
              : this.renderMoreProducts()}
        </platform-menu-sidebar-panel>
      `,
    );
  }

  // The overlay's More Products submenu, panel-shaped: the catalog products the
  // site is not entitled to, linking out to their product information pages.
  private renderMoreProducts() {
    const { moreProducts } = resolveFirstLevelExtras(
      this._products.value ?? [],
      this.launchDarklyController.flags,
      this._sites.value,
      this._currentSite.value,
    );
    return html`
      <menu class="more-products">
        ${repeat(
          moreProducts,
          (product) => product.slug,
          (product) => html`
            <li>
              <a href=${product.productInformationUrl} target="_blank" rel="noopener noreferrer">
                <platform-product-icon productSlug=${product.slug}></platform-product-icon>
                <span>${t(product.slug, product.name)}</span>
              </a>
            </li>
          `,
        )}
      </menu>
    `;
  }

  /**
   * Reporting, Automation and PathfinderAI render in English in every locale by
   * product decision. More Products and Phantom Labs sit in the same list and
   * stay translatable — the split is deliberate, so don't wrap the English ones
   * back in msg().
   */
  private renderFirstLevel(products: ProductMenuItem[]) {
    const flags = this.launchDarklyController.flags;
    // Same URL-path resolution the overlay uses; platform-home means "no product".
    const currentProduct = resolveCurrentProduct(products, window.location.pathname);
    const { showReporting, showAutomation, showPathfinderAi, showPhantomLabs, moreProducts } = resolveFirstLevelExtras(
      products,
      flags,
      this._sites.value,
      this._currentSite.value,
    );

    return html`
      <ul>
        ${repeat(
          products,
          (product) => product.slug,
          (product) => html`
            <li>
              ${this.railTooltip(
                t(product.slug, product.name),
                html`<button
                  type="button"
                  class=${product.slug === currentProduct.slug ? 'current-product' : ''}
                  data-dd-action-name="Sidebar Menu ${product.name}"
                  aria-current=${ifDefined(
                    // `true`, not `page`: this is a disclosure trigger that opens the
                    // second-level panel, not a link to the current page. Confirmed
                    // with the SDS-6757 reporter. The panel's own links do use `page`.
                    product.slug === currentProduct.slug ? 'true' : undefined,
                  )}
                  aria-expanded=${ifDefined(
                    // Products without menu content keep an inert button: no
                    // expanded state, matching the missing chevron below.
                    hasProductMenu(product.slug)
                      ? typeof this._openPanel === 'object' && this._openPanel.slug === product.slug
                        ? ('true' as const)
                        : ('false' as const)
                      : undefined,
                  )}
                  @click=${(event: MouseEvent) => this.onProductClick(product, event)}
                >
                  <platform-product-icon productSlug=${product.slug}></platform-product-icon>
                  <span>${t(product.slug, product.name)}</span>
                  ${hasProductMenu(product.slug)
                    ? html`<btux-svg class="expand-indicator" name="chevron-right"></btux-svg>`
                    : nothing}
                </button>`,
              )}
            </li>
          `,
        )}
      </ul>
      ${showReporting || showAutomation || showPathfinderAi || showPhantomLabs || moreProducts.length > 0
        ? html`
            <hr />
            <ul>
              ${showReporting
                ? html`
                    <li>
                      ${this.railTooltip(
                        'Reporting',
                        html` <button
                          type="button"
                          data-dd-action-name="Sidebar Menu Reporting"
                          aria-expanded=${this._openPanel === 'reporting'}
                          @click=${this.onReportingClick}
                        >
                          <btux-svg name="reporting"></btux-svg>
                          <span>Reporting</span>
                          <btux-svg class="expand-indicator" name="chevron-right"></btux-svg>
                        </button>`,
                      )}
                    </li>
                  `
                : nothing}
              ${showAutomation
                ? html`
                    <li>
                      ${this.railTooltip(
                        'Automation',
                        html` <a href="/automation">
                          <btux-svg name="bt-workflows"></btux-svg>
                          <span>Automation</span>
                        </a>`,
                      )}
                    </li>
                  `
                : nothing}
              ${showPathfinderAi
                ? html`
                    <li>
                      <!-- No tags on first-level rows: they truncate badly in the
                           rail. Revisit with a treatment that survives narrow rows. -->
                      ${this.railTooltip(
                        'PathfinderAI',
                        html` <a href="/chat">
                          <btux-svg name="ai-pathfinder"></btux-svg>
                          <span>PathfinderAI</span>
                        </a>`,
                      )}
                    </li>
                  `
                : nothing}
              ${showPhantomLabs
                ? html`
                    <li>
                      ${this.railTooltip(
                        msg('Phantom Labs', { id: 'menu-phantom-labs' }),
                        html` <a href="/phantom-labs">
                          <btux-svg name="bt-logo"></btux-svg>
                          <span>${msg('Phantom Labs', { id: 'menu-phantom-labs' })}</span>
                        </a>`,
                      )}
                    </li>
                  `
                : nothing}
              ${moreProducts.length > 0
                ? html`
                    <li>
                      ${this.railTooltip(
                        msg('More Products'),
                        html` <button
                          type="button"
                          data-dd-action-name="Sidebar Menu More Products"
                          aria-expanded=${this._openPanel === 'more-products'}
                          @click=${this.onMoreProductsClick}
                        >
                          <btux-svg name="circle-plus"></btux-svg>
                          <span>${msg('More Products')}</span>
                          <btux-svg class="expand-indicator" name="chevron-right"></btux-svg>
                        </button>`,
                      )}
                    </li>
                  `
                : nothing}
            </ul>
          `
        : nothing}
    `;
  }

  // Rendered outside the scrollable list area so it stays anchored to the
  // bottom of the rail regardless of how long the menus above grow.
  private renderAdmin(products: ProductMenuItem[]) {
    const { adminSite, showAdminLink } = resolveFirstLevelExtras(
      products,
      this.launchDarklyController.flags,
      this._sites.value,
      this._currentSite.value,
    );
    if (!showAdminLink || !adminSite) {
      return nothing;
    }
    return html`
      <div class="admin">
        <hr />
        <ul>
          <li>
            ${this.railTooltip(
              msg('Administration', { id: 'admin' }),
              html` <button
                type="button"
                data-dd-action-name="admin-menu-link"
                @click=${() => selectSite(adminSite.site_id)}
              >
                <platform-product-icon productSlug="admin"></platform-product-icon>
                <span>${msg('Administration', { id: 'admin' })}</span>
              </button>`,
            )}
          </li>
        </ul>
      </div>
    `;
  }

  // The collapsed rail is icon-only; each row's title shows as a hover/focus
  // tooltip. Disabled while expanded (the label is visible) and hoisted so the
  // rail's scroll clipping can't cut the bubble off.
  private railTooltip(label: string, row: TemplateResult<1>) {
    return html` <sl-tooltip content=${label} placement="right" hoist ?disabled=${this.expanded}>${row}</sl-tooltip> `;
  }

  private toggleExpanded() {
    this.expanded = !this.expanded;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [MENU_SIDEBAR_TAG]: PlatformMenuSidebar;
  }
}
